/**
 * The cross-CRF registers' wire layer — Figma page 04 (v7 Block B).
 *
 * ===========================================================================
 * TRANSCRIBED FROM THE SERVICE, NOT GUESSED
 * ===========================================================================
 * Both row shapes below are copied field-for-field from
 * `apps/api/src/modules/registers/registers.service.ts` (`TestRoundRegisterRow`,
 * `DlFileRegisterRow`), and the envelope from `countries/support/http`'s `paged`.
 * `created_at` is a `Date` on the server and a STRING on the wire — JSON has no
 * date type — so it is typed as the string these screens actually receive.
 *
 * ===========================================================================
 * NO MONEY CROSSES THIS BOUNDARY
 * ===========================================================================
 * Neither row carries `amount` or any other money field, so the masking
 * interceptor is not engaged on these routes and nothing here needs the
 * `isMaskedAmount` discipline. If a "value" column ever appears on one of these
 * registers, this comment is where the reader learns that the screens were
 * written on the assumption it could not.
 *
 * ===========================================================================
 * THE BYTES HAVE ONE ROUTE, AND IT IS NOT OURS
 * ===========================================================================
 * `registers.controller.ts` deliberately ships NO download route: a DL file's
 * bytes already have one — `GET /work-orders/:id/files/:fileId` — and the
 * register carries `work_order_id` so the client can address it. A second byte
 * route would be a second place for the containment and belonging checks to
 * drift apart. {@link downloadRegisterFile} therefore addresses that existing
 * route, and it goes through `fetchBinary` for the reason `downloadWorkFile`
 * and `downloadCrfFile` both record: the route is behind the JWT guard, and a
 * bare `<a href>` carries no `Authorization` header, so it would 401 and read
 * as a download that silently does nothing.
 */

import { ApiRequestError, ApiTransportError, apiRequest, fetchBinary } from '../../lib/api';
// `work_type` is not in `@crf/shared` — it is an ENUM on the API's `Crf` entity — so the
// web declares it once in the CRF feature and a mirror test holds it to the server.
import type { WorkType } from '../crfs/domain';
import type { BinaryResponse } from '../../lib/api';

/* ─────────────────────────────────────────────────────────────────── DTOs ── */

/** `test_rounds.result` — the closed vocabulary migration 6 declares. */
export const TEST_RESULTS = ['in_test', 'pass', 'fail'] as const;
export type TestResult = (typeof TEST_RESULTS)[number];

export interface Paged<T> {
  data: T[];
  page: number;
  perPage: number;
  total: number;
}

/** `TestRoundRegisterRow` — registers.service.ts:52-63. */
export interface TestRoundRegisterRow {
  id: string;
  crf_id: string;
  crf_doc_no: string;
  /**
   * WHICH KIND OF WORK — `crfs.work_type` (migration 37, design §5.2a).
   *
   * ⚠ THIS REGISTER ALWAYS CONTAINED PROJECTS. Its read has no predicate that could have
   * excluded one, so from the day migration 37 shipped it widened whether or not anybody
   * decided it should. Carrying the field is what turns that from a silent widening into
   * a stated one: a reader can now tell which rows are which, and a `?work_type=` filter
   * exists to narrow it deliberately.
   */
  work_type: WorkType;
  party_name: string;
  /** The work order the round belongs to — what the row's action links to. */
  work_order_id: string;
  round_no: number;
  result: TestResult;
  tester: { id: string; name: string };
  /** On a FAILURE this is the reason. It is the point of the screen. */
  note: string | null;
  created_at: string;
}

/** `DlFileRegisterRow` — registers.service.ts:65-83. */
export interface DlFileRegisterRow {
  id: string;
  crf_id: string;
  crf_doc_no: string;
  /**
   * WHICH KIND OF WORK — `crfs.work_type` (migration 37, design §5.2a).
   *
   * ⚠ THIS REGISTER ALWAYS CONTAINED PROJECTS. Its read has no predicate that could have
   * excluded one, so from the day migration 37 shipped it widened whether or not anybody
   * decided it should. Carrying the field is what turns that from a silent widening into
   * a stated one: a reader can now tell which rows are which, and a `?work_type=` filter
   * exists to narrow it deliberately.
   */
  work_type: WorkType;
  party_name: string;
  /** Half of the byte address; see the file header. */
  work_order_id: string;
  file_name: string;
  mime: string | null;
  size_bytes: number;
  /**
   * WHO UPLOADED IT. `attachments.created_by` is `ON DELETE SET NULL`, so a file
   * that outlived its uploader reports `null` — and the screen must render that
   * as unknown rather than borrowing any other name from the row.
   */
  uploaded_by: { id: string; name: string } | null;
  created_at: string;
}

/* ─────────────────────────────────────────────────────────────────── reads ── */

export interface TestingRegisterFilters {
  /**
   * PASSED THROUGH UNVALIDATED, deliberately.
   *
   * The server owns this vocabulary and answers 400 naming it for a stranger
   * value. Sanitising the parameter here would mean a URL saying `?result=bogus`
   * quietly rendered the WHOLE register while claiming to be filtered — and on
   * this screen, where the question is "show me the failures", a silently
   * unfiltered list is the one wrong answer that looks right.
   */
  result?: string;
  tester?: string;
  page?: number;
  perPage?: number;
}

export async function fetchTestingRegister(
  countryCode: string,
  filters: TestingRegisterFilters = {},
  signal?: AbortSignal,
): Promise<Paged<TestRoundRegisterRow>> {
  return apiRequest<Paged<TestRoundRegisterRow>>(`/countries/${countryCode}/testing`, {
    query: {
      result: filters.result,
      tester: filters.tester,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

/** `crf` and `uploader` are the route's own filters; no screen sets them yet. */
export interface DlFilesRegisterFilters {
  crf?: string;
  uploader?: string;
  page?: number;
  perPage?: number;
}

export async function fetchDlFilesRegister(
  countryCode: string,
  filters: DlFilesRegisterFilters = {},
  signal?: AbortSignal,
): Promise<Paged<DlFileRegisterRow>> {
  return apiRequest<Paged<DlFileRegisterRow>>(`/countries/${countryCode}/dl-files`, {
    query: {
      crf: filters.crf,
      uploader: filters.uploader,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

/* ─────────────────────────────────────────────────────────────────── bytes ── */

/**
 * One DL file's bytes, off the work-order route that already serves them.
 *
 * The path is built from the register row's own `work_order_id` and `id` — the
 * attachment id IS the file id on that route, which is why the register carries
 * both. See the file header for why there is no `/dl-files/:id` to call instead.
 */
export async function downloadRegisterFile(
  workOrderId: string,
  fileId: string,
): Promise<BinaryResponse> {
  return fetchBinary(`/work-orders/${workOrderId}/files/${fileId}`);
}

/* ────────────────────────────────────────────────────────────────── errors ── */

/**
 * Register failures, turned into sentences an operator can act on.
 *
 * Re-authored per feature rather than imported from `features/crfs/errors` —
 * the house pattern the quotes/work/CRF siblings all keep, and for the reason
 * they each record: a shared helper would grow a branch per feature until
 * nobody could tell which sentence any surface produces. The rules those
 * siblings keep hold here too: a 403 never names the permission key, and the
 * server's own wording is PASSED THROUGH on the statuses where it is the most
 * precise thing available.
 *
 * 400 is in that pass-through set for this feature specifically. `?result=` is
 * validated against the enum by the service, which answers
 * "result must be one of: in_test, pass, fail." — a sentence that names the
 * vocabulary. A house sentence here would replace the only text that says what
 * to type instead.
 */
export function registerErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was changed.';
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') {
      return 'That register belongs to another country.';
    }
    if (error.status === 404) return 'That record does not exist.';
    if (error.status === 403) {
      return 'This account is not allowed to do that in this country.';
    }
    return error.message;
  }
  return 'Something went wrong. Nothing was changed.';
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const registerKeys = {
  testing: (cc: string, filters: TestingRegisterFilters) =>
    ['registers', cc, 'testing', filters] as const,
  dlFiles: (cc: string, filters: DlFilesRegisterFilters) =>
    ['registers', cc, 'dl-files', filters] as const,
};
